<template>
    <!-- *********************************************
    *              Origin Account Block              *
    ********************************************** -->
    <divider-generic dividerSize="medium" icon="account-balance" :title="t('payments.voucher.step1.originAccountDivider')" :applyBorder="false"/>
    <dropdown-account
    id="originAccount"
    :params="originAccountParams"
    :options="accountsBalances"
    :selectedOption="formData.originAccount"
    @clicked="[
        emit('formDataChange', { field: 'originAccount', inputValue: $event }),
        emit('formDataChange', { field: 'currency', inputValue: accountsBalances.find(account => account.id === $event).currency })]"/>
    <!-- *********************************************
    *               Payment Data Block               *
    ********************************************** -->
    <divider-generic dividerSize="medium" icon="payment-single" :title="t('payments.voucher.step1.paymentDivider')"/>
    <dropdown-generic
    id="code"
    :params="codeParams"
    :options="parseVouchersList()"
    :selectedOption="formData.code"
    @clicked="[emit('formDataChange', { field: 'code', inputValue: $event}), emit('formDataChange', { field: 'amount', inputValue: vouchers.find(ele => ele.code === formData.code).amount}), clearFormErrors('code')]"/>

    <!-- Amount Text Block -->
    <text-block-generic
    v-if="formData.code && formData.code !== '00'"
    id="amount"
    :params="{
        ...amountParams,
        content: amountFormat(vouchers.find(ele => ele.code === formData.code).amount, formData.currency)
    }"/>

    <!-- Amount Input -->
    <input-amount
    v-if="formData.code && formData.code === '00'"
    id="amount"
    :params="{
        ...amountParams,
        mandatory: true,
        labelText: amountParams.label,
        currency: formData.currency
    }"
    :inputValue="formData.amount"
    @input="amountInputHandler"
    @onBlur="amountInputBlurValidation"/>
</template>

<script setup>
import DividerGeneric from '@/components/generic/dividers/Generic'
import DropdownAccount from '@/components/generic/dropdowns/accounts/Account'
import DropdownGeneric from '@/components/generic/dropdowns/generic/Generic'
import TextBlockGeneric from '@/components/generic/textBlocks/Generic'
import InputAmount from '@/components/generic/inputs/Amount'

/* Vue */
import { ref, watch } from 'vue'

/* Assets */
import { amountFormat } from '@/assets/scripts/amounts'
import { getEmptyFieldsIdentifiers } from '@/assets/scripts/objects'
import { getElementOffsetTopPosition } from '@/assets/scripts/htmlManipulators'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['formDataChange', 'formErrorsCleared', 'mandatoryListChangeHandler', 'resetShowMandatoryErrorsProp', 'scrollTo'])

/* props received
@accountsBalances { Array }
@vouchers { Array }
@clearFormErrors { Boolean } - Prop to remove of form errors
@formData { Object } - object with info for our current form
@mandatoryList { Array } Array of mandatory keys corresponding with our form
@showMandatoryErrors { Boolean } Prop to trigger display of mandatory errors
*/
const props = defineProps({
    accountsBalances: Array,
    vouchers: Array,
    clearFormErrors: Boolean,
    formData: Object,
    mandatoryList: Array,
    showMandatoryErrors: Boolean
})

/* Data */
const originAccountParams = ref({
	labelText: t('payments.voucher.step1.originAccountLabel'),
	mandatory: true,
	errorState: false,
    errorMessage: null
})

const codeParams = ref({
	labelText: t('payments.voucher.step1.vouchersLabel'),
    placeholder: t('payments.voucher.step1.vouchersPlaceholder'),
	mandatory: true,
	errorState: false,
    errorMessage: null
})

const amountParams = ref({
	label: t('payments.voucher.step1.amountLabel'),
	errorState: false,
    errorMessage: null
})

const parseVouchersList = () => {
    return props.vouchers.map(ele => {
        return {
            ...ele,
            id: ele.code,
            text: ele.amountInUnitsDescription
        }
    })
}

/**********************************************************************
*                                amount                               *
***********************************************************************/
const amountInputHandler = $event => {
    emit('formDataChange', { field: 'amount', inputValue: $event })
    clearFormErrors('amount')
}

const amountInputBlurValidation = () => {
    if (props.formData.amount && props.formData.amount.length && parseInt(props.formData.amount) === 0) {
        allFormFields.value.amountParams.errorState = true
        allFormFields.value.amountParams.errorMessage = t('invalidAmount')
    }
}

/**********************************************************************
*                                                                     *
*                        MANDATORY FIELDS LOGIC                       *
*                                                                     *
***********************************************************************/
const allFormFields = ref({
	originAccountParams: originAccountParams.value,
    codeParams: codeParams.value,
    amountParams: amountParams.value
})

watch(() => props.showMandatoryErrors, newValue => {
	if (newValue) showMandatoryErrorsHandler()
    emit('resetShowMandatoryErrorsProp')
})

const showMandatoryErrorsHandler = () => {
	/* Logic to scroll to first emptyField */

	const mandatoryFieldsEmptyList = getEmptyFieldsIdentifiers(props.formData, props.mandatoryList)

    if ((props.formData.amount && props.formData.amount.length && parseInt(props.formData.amount) === 0)) {
        emit('scrollTo', getElementOffsetTopPosition('amount'))
        amountInputBlurValidation()
    }

	if (mandatoryFieldsEmptyList.length) {
        emit('scrollTo', getElementOffsetTopPosition(mandatoryFieldsEmptyList[0]))

        mandatoryFieldsEmptyList.forEach(fieldName => {
            allFormFields.value[`${fieldName}Params`].errorState = true
            allFormFields.value[`${fieldName}Params`].errorMessage = t('mandatoryFieldError')
        })
    }
}

/**********************************************************************
*                                                                     *
*                          CLEAR ERRORS LOGIC                         *
*                                                                     *
***********************************************************************/
watch(() => props.clearFormErrors, () => {
    clearFormErrors()
    emit('formErrorsCleared')
})

const clearFieldError = (fieldName) => {
    allFormFields.value[fieldName].errorMessage = null
    allFormFields.value[fieldName].errorState = false
}

const clearFormErrors = () => {
    for (var fieldName in allFormFields.value) {
        clearFieldError(fieldName)
    }
}
</script>
